import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question:
      "Can you have severe depression and still function at a high level?",
    answer:
      "Yes. The diagnostic criteria for major depressive disorder measure the number and intensity of symptoms, not whether you can hold a job or care for a family. Someone who gets up, works a full day, and meets their obligations can still meet every criterion for severe, recurrent depression. Functioning isn't evidence against the diagnosis; it's evidence of how much effort you're spending to hide it.",
  },
  {
    question: "What is high-functioning depression?",
    answer:
      "High-functioning depression is not a formal diagnosis, but it describes a real and common pattern: people whose depression is serious by every clinical measure, but who have built enough structure and obligation around themselves that the illness stays invisible to others. The functioning has a price, and the person with depression is the one paying it.",
  },
  {
    question:
      "Why does high-functioning depression often go untreated for years?",
    answer:
      "Because you look fine, no one asks. And because no one asks, you don't say. Many people tell themselves it can't be that bad because they're still managing to do everything. That reasoning feels logical, but it's one of the main reasons people with severe depression go untreated for years.",
  },
  {
    question: "If I'm still functioning, do I still qualify for treatment?",
    answer:
      "Yes. You qualify based on your history and your symptoms, not on whether you've stopped functioning. If you've had two or more medication trials and still feel like you're operating at a fraction of your capacity, that may be treatment-resistant depression, regardless of how it looks from the outside.",
  },
  {
    question: "How is SAINT different from standard TMS for busy people?",
    answer:
      "Standard TMS requires one session a day, five days a week, for six to nine weeks — 30 to 36 clinic visits that can be a real obstacle for someone with a job and a family. SAINT is delivered over five consecutive days, with ten 10-minute sessions per day. That's one week off work instead of two months of daily appointments.",
  },
  {
    question: "Do I have to stop my medication to do SAINT?",
    answer:
      "No. Most patients continue their existing medication regimen through treatment. SAINT is not a replacement for what you've built — it is a different kind of intervention aimed at the brain circuitry your medications may not have reached. Never change medication without guidance from your prescribing provider.",
  },
];

const related: RelatedLink[] = [
  {
    title: "High-Functioning Depression Symptoms: 10 Quiet Signs",
    href: "/high-functioning-depression-symptoms-treatment",
    description:
      "10 everyday habits that can be quiet signs of high-functioning depression.",
  },
  {
    title: "Why Am I So Tired All the Time? Depression Fatigue",
    href: "/learn/always-tired-depression-fatigue",
    description:
      "How depression drains your energy and why rest doesn't fix it.",
  },
  {
    title: "Can Depression Cause Physical Pain?",
    href: "/learn/can-depression-cause-physical-pain",
    description: "Why depression hurts and what helps with the aches.",
  },
  {
    title: "Can't Stop Crying for No Reason?",
    href: "/learn/cant-stop-crying-for-no-reason",
    description: "What frequent crying can mean and when to get help.",
  },
  {
    title: "You've Tried Everything. Why Is the Depression Still Here?",
    href: "/youve-tried-everything-why-is-the-depression-still-here",
    description:
      "What treatment-resistant depression means and why medications don't work for everyone.",
  },
  {
    title: "What Is SAINT TMS Therapy?",
    href: "/learn/what-is-saint-tms",
    description: "How the five-day, fMRI-guided protocol works.",
  },
];

export default function HighFunctioningDepression() {
  return (
    <DepressionContentPage
      seo={{
        title:
          "High-Functioning Depression: When Holding It Together Is the Problem | Neuro Wellness",
        description:
          "Functioning isn't the same as being okay. Learn how high-functioning depression hides in plain sight, why treatment stalls, and how SAINT TMS fits a full life.",
        canonical:
          "/high-functioning-depression-when-holding-it-together-is-the-problem",
        keywords:
          "high-functioning depression, hidden depression, treatment-resistant depression, SAINT TMS, depression while working, persistent depressive disorder, anhedonia, functioning depression treatment",
      }}
      badge="Clinical Insights"
      heroTitle="High-Functioning Depression: When Holding It Together Is the Problem"
      heroSubtitle="From the outside, your life looks fine. From the inside, you haven't enjoyed anything in months. Functioning is not the same as being okay — and it's one of the main reasons severe depression goes untreated for years."
      lastUpdated="2026-11-03"
      sections={[
        {
          heading: "Functioning is not the same as being okay",
          body: (
            <div className="space-y-4">
              <p>
                From the outside, your life looks fine. Better than fine, maybe.
                You go to work every day. You're married or partnered. You have
                kids who need you, and you show up for them. You have friends
                who'd say you're doing well.
              </p>
              <p>
                From the inside, it's a different story. You haven't enjoyed
                anything in months. You're exhausted in a way that sleep doesn't
                fix. You're irritable with the people you love most and then
                ashamed of it. You forget things. You avoid things. You lie
                awake at night with your mind spinning, and you get up in the
                morning and do it all again.
              </p>

              {/* Visual 5: What it can look like */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <div className="relative aspect-[4/3] sm:aspect-[16/10] w-full overflow-hidden bg-muted">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/5b16a42e-3117-4706-9418-ec235a4e3c1e.png"
                    alt="Infographic: What high-functioning depression can look like — still showing up, constant exhaustion, perfectionism, irritability, trouble sleeping, feeling empty"
                    className="h-full w-full object-contain bg-white"
                    loading="lazy"
                  />
                </div>
                <div className="p-3.5 bg-card border-t border-border">
                  <p className="font-headings text-sm font-bold text-primary">
                    Signs High-Functioning Depression Hides in Plain Sight
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    Still showing up at work or home doesn't mean you're okay.
                    Constant exhaustion, emotional flatness, and sleeplessness
                    often hide behind a polished exterior.
                  </p>
                </div>
              </div>

              <p>
                You've probably told yourself that it can't be that bad, because
                look at everything you're still managing to do. That reasoning
                feels logical. It's also one of the main reasons people with
                severe depression go untreated for years.
              </p>
              <p>
                Here is something a lot of people don't realize: the diagnostic
                criteria for major depressive disorder say nothing about whether
                you can hold a job. Severity is measured by the number and
                intensity of symptoms, not by how much you're still managing to
                do despite them.
              </p>
              <p>
                Someone who gets up, works a full day, cooks dinner, helps with
                homework, and collapses into bed with a hollow feeling can meet
                every criterion for severe, recurrent depression. The
                functioning isn't evidence against the diagnosis. It's evidence
                of how much effort you're spending to hide it.
              </p>
              <p>
                Clinicians sometimes call this "high-functioning depression."
                It's not a formal diagnosis, but it describes a real and common
                pattern: people whose depression is serious by every clinical
                measure, but who have built enough structure and obligation
                around themselves that the illness stays invisible.
              </p>
            </div>
          ),
        },
        {
          heading: "What it actually costs",
          body: (
            <div className="space-y-4">
              <p>
                The trouble with high-functioning depression is that the
                functioning has a price, and you're the one paying it.
              </p>

              {/* Visual 4: The Cycle (Pressure -> Push through -> Emotional fatigue -> Withdraw -> Self-criticism -> Repeat) */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <div className="relative aspect-[4/3] sm:aspect-[16/10] w-full overflow-hidden bg-muted">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/0b0c89aa-bc14-4b10-b8cb-a88b4e26b1f0.png"
                    alt="Diagram of the high-functioning depression cycle: Pressure to perform, pushing through, emotional fatigue, withdrawing in private, self-criticism, and repeating"
                    className="h-full w-full object-contain bg-white"
                    loading="lazy"
                  />
                </div>
                <div className="p-3.5 bg-card border-t border-border">
                  <p className="font-headings text-sm font-bold text-primary">
                    The High-Functioning Depression Cycle
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    Same patterns, a heavier you. Constant pressure to perform
                    leads to pushing through, emotional exhaustion, private
                    withdrawal, and self-blame.
                  </p>
                </div>
              </div>

              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong>Anhedonia.</strong> You do the things, but you don't
                  feel them. Your child laughs and you notice it from a
                  distance. Dinner with friends is something to get through.
                </li>
                <li>
                  <strong>Irritability.</strong> The energy it takes to hold
                  everything together leaves nothing for patience. Small
                  frustrations turn into snapping, then guilt.
                </li>
                <li>
                  <strong>Cognitive load.</strong> Concentration and memory
                  suffer. You reread the same email three times. You forget a
                  commitment and feel like a failure.
                </li>
                <li>
                  <strong>Physical toll.</strong> Headaches, stomach problems,
                  insomnia. The body keeps score even when you're refusing to.
                </li>
                <li>
                  <strong>Isolation.</strong> Because you look fine, no one
                  asks. And because no one asks, you don't say.
                </li>
              </ul>

              {/* Visual 3: When it may be more than stress checklist */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <div className="relative aspect-[4/3] sm:aspect-[16/10] w-full overflow-hidden bg-muted">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/787fec62-d7d7-494e-830e-b5d025cf6d78.png"
                    alt="Checklist: When it may be more than stress — low mood that lingers, less joy in daily life, sleep or appetite changes, hard to slow down, feeling numb, daily life feels heavier than it looks"
                    className="h-full w-full object-contain bg-white"
                    loading="lazy"
                  />
                </div>
                <div className="p-3.5 bg-card border-t border-border">
                  <p className="font-headings text-sm font-bold text-primary">
                    Recognizing When It's More Than Everyday Stress
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    If low mood lingers, sleep is disrupted, and daily life
                    consistently feels heavier than it looks on paper, it's time
                    to evaluate the root cause.
                  </p>
                </div>
              </div>

              <p>
                Many people in this situation eventually do seek help, but they
                wait until something forces the issue. A comment from a spouse.
                A mistake at work. A moment with their kids that should have
                been joyful and wasn't.
              </p>
            </div>
          ),
        },
        {
          heading: "Why treatment often stalls",
          body: (
            <div className="space-y-4">
              <p>
                People with high-functioning depression tend to get a certain
                kind of treatment: a therapist, an SSRI, maybe something for
                sleep or anxiety layered on top. And often it helps, partly.
                Enough to keep functioning. Not enough to actually feel well.
              </p>

              {/* Visual 2: Ways to get support pathway */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <div className="relative aspect-[4/3] sm:aspect-[16/10] w-full overflow-hidden bg-muted">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/d898d4f0-d250-449c-9cdf-5875ae7194ab.png"
                    alt="Step-by-step roadmap: Ways to get support — 1 Talk therapy, 2 Medication review, 3 Lifestyle support, 4 TMS, 5 SAINT accelerated TMS, 6 Personalized care plan"
                    className="h-full w-full object-contain bg-white"
                    loading="lazy"
                  />
                </div>
                <div className="p-3.5 bg-card border-t border-border">
                  <p className="font-headings text-sm font-bold text-primary">
                    Expanding Your Care Plan Beyond One Tool
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    A comprehensive mental health path can integrate talk
                    therapy, medication review, neuromodulation like TMS or
                    SAINT® accelerated TMS, and personalized care.
                  </p>
                </div>
              </div>

              <p>
                Because you're still going to work, the partial response gets
                treated as success. The dose gets adjusted, a second medication
                gets added, and you learn to live at 60 percent. Nobody uses the
                phrase "treatment-resistant," because you don't look like
                someone who's resisting treatment. You look like someone who's
                coping.
              </p>
              <p>
                But if you've had two or more medication trials and still feel
                like this, that's exactly what it is.
              </p>
            </div>
          ),
        },
        {
          heading: "Why medication alone often leaves you at 60 percent",
          body: (
            <div className="space-y-4">
              <p>
                Antidepressants work on brain chemistry. But depression is also
                a circuitry problem. Brain imaging consistently shows that the
                region behind the forehead called the dorsolateral prefrontal
                cortex, which regulates mood, motivation, and attention, is
                underactive in depression. Medication changes the chemical
                environment around that circuit. It doesn't directly retrain it.
              </p>
              <p>
                That's why someone can take their medication faithfully,
                function at a high level, and still feel fundamentally unchanged
                underneath. The chemistry is being managed. The circuit is still
                underperforming.
              </p>
              <p>
                Transcranial magnetic stimulation (TMS) works on the circuit
                itself, using focused magnetic pulses to stimulate that
                underactive region directly. There's no anesthesia and nothing
                enters your bloodstream. The barrier for busy people has always
                been time: standard TMS requires one session a day, five days a
                week, for six to nine weeks. For someone with a job and a
                family, 30 to 36 clinic visits is a real obstacle.
              </p>
            </div>
          ),
        },
        {
          heading: "A treatment that fits a full life",
          body: (
            <div className="space-y-4">
              <div className="my-6 grid gap-4 sm:grid-cols-2 not-prose">
                <div className="overflow-hidden rounded-xl border border-border bg-card shadow-soft">
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                    <img
                      src="https://vibe.filesafe.space/1787327131284421342/attachments/61fd8a15-3767-464e-99b6-5a1351dea1b3.png"
                      alt="Personalized fMRI brain scan targeting for SAINT® TMS therapy"
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3.5">
                    <p className="font-headings text-sm font-bold text-primary">
                      Personalized fMRI Brain Targeting
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Custom targeting guided by resting-state fMRI maps to fit
                      your unique brain wiring.
                    </p>
                  </div>
                </div>

                <div className="overflow-hidden rounded-xl border border-border bg-card shadow-soft">
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                    <img
                      src="https://vibe.filesafe.space/1787327131284421342/attachments/9907b0c5-c62d-4096-ba03-9d7a9cc3ffc5.png"
                      alt="Patient seated comfortably in SAINT® treatment suite during stimulation series"
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3.5">
                    <p className="font-headings text-sm font-bold text-primary">
                      Five-Day Stimulation Series
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Completed in 5 consecutive days—ten 10-minute sessions per
                      day with zero downtime.
                    </p>
                  </div>
                </div>
              </div>

              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is an accelerated, FDA-cleared form of TMS developed at
                Stanford's Brain Stimulation Lab. It's indicated for adults with
                major depressive disorder who haven't gotten adequate relief
                from antidepressants in their current episode. And it was built
                around a schedule that a working parent can actually manage.
              </p>
              <p>
                Here's how it works at a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-bold text-accent underline underline-offset-2 hover:text-accent/80"
                >
                  Verified SAINT® Provider
                </Link>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-5 space-y-2">
                <li>
                  <strong>Consultation (Zoom web conference).</strong> You meet
                  with our clinical team over a convenient Zoom call. As your
                  evaluation evolves, you will also have the option to meet with
                  our psychiatrist by Zoom web conference or in person at our
                  clinic. You meet with our clinical team to review your
                  history, your medication trials, and your current symptoms,
                  and to confirm you're a candidate. We take what you're
                  carrying seriously, regardless of how it looks from the
                  outside.
                </li>
                <li>
                  <strong>fMRI brain scan (mandatory in person).</strong> A
                  roughly 45-minute functional MRI maps how your individual
                  brain networks are wired and where they're communicating
                  poorly. (Mandatory in-person scan at an imaging partner
                  center.)
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  reads your scan and identifies the exact location where
                  stimulation will most effectively reach the mood-regulation
                  network. Neuronavigation guides the coil to that spot for
                  every session.
                </li>
                <li>
                  <strong>
                    Five-day stimulation series (mandatory in person).
                  </strong>{" "}
                  In-person treatment Monday through Friday at our Coral Springs
                  clinic. Ten 10-minute sessions per day, with 50-minute breaks
                  in between, for five consecutive days. That's one week off
                  work, or one week of arranged childcare, instead of two months
                  of daily appointments. Most patients notice a change in mood
                  within the first couple of days.
                </li>
              </ol>

              {/* Dedicated Private Suites Experience */}
              <div className="overflow-hidden rounded-xl border border-border bg-card shadow-soft not-prose my-6">
                <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/6222b5e9-ba0f-42b4-a0c1-50b2512c1da5.png"
                    alt="Private furnished suite in the SAINT Lounge Wing with couch, leather recliner, workstation, and kitchen access"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="font-headings text-sm font-bold text-primary">
                    Private Suites &amp; Dedicated SAINT® Lounge Wing
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    Designed for comfort, privacy, and full outpatient
                    convenience. During your 50-minute breaks, relax in your own
                    private suite, take video calls from a quiet work desk, have
                    lunch, or spend time with family. Our on-site care team
                    ensures you feel right at home all day.
                  </p>
                </div>
              </div>
            </div>
          ),
        },
        {
          heading: "What the results look like",
          body: (
            <div className="space-y-4">
              <SaintComparisonStatBand />
              <p>
                In the clinical trials behind SAINT's FDA clearance,
                participants had treatment-resistant depression and had already
                failed prior treatments. Seventy-nine percent reached remission,
                not just improvement, with an average time to remission of about
                two and a half days. Across more than 400 trial patients, 85
                percent experienced symptom relief, with no serious adverse
                events.
              </p>
              <p>
                Side effects were generally mild: headache, fatigue, and
                temporary scalp discomfort at the treatment site. SAINT isn't
                appropriate for people with non-removable metal in or near the
                head or an implanted device, and you'll be screened for these at
                your consultation.
              </p>
              <p>
                You don't have to stop your current medications. Most patients
                continue their existing regimen through treatment. And you don't
                have to fall apart first. You qualify based on your history and
                your symptoms, not on whether you've stopped functioning.
              </p>
            </div>
          ),
        },
        {
          heading: "Permission you might need to hear",
          body: (
            <div className="space-y-4">
              <p>
                You are allowed to want more than functioning. Getting through
                the day is not the goal of treatment. Feeling like yourself
                again is. If the people around you would be shocked to learn how
                bad it actually is, that's not a reason to keep hiding it. It's
                a reason to get it treated properly.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                If you've been holding it together for a long time and you're
                tired, a consultation is one hour that might change the
                trajectory of everything after it.
              </p>

              {/* Visual 1: Consultation / Supportive Clinical Setting */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/013d2a77-430e-45a9-b070-497bf00ec70f.png"
                    alt="Compassionate private mental health consultation in a calm, welcoming outpatient setting"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-3.5 bg-card border-t border-border">
                  <p className="font-headings text-sm font-bold text-primary">
                    A Confidential, One-on-One Evaluation
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    A consultation is an open conversation about your history,
                    pressures, and goals—never an obligation. We take the time
                    to listen and map out what kind of care makes the most
                    sense.
                  </p>
                </div>
              </div>

              <p>
                At Neuro Wellness TMS Centers of America, the first appointment
                is an evaluation, not a commitment. We offer private outpatient
                care with a dedicated care team and weekend availability,
                because we know your schedule is already full. We'll review your
                history and symptoms and tell you honestly whether SAINT is a
                good fit. If it is, we'll walk you through the scan, the
                schedule, and what to expect during your treatment week.
                Insurance coverage for SAINT varies by plan; our team can help
                you understand your options, including prior authorization and
                self-pay.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If you are in crisis or thinking about harming yourself, please
                call or text 988 (Suicide and Crisis Lifeline) or go to your
                nearest emergency room. This article is for educational purposes
                and is not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
